import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { omit } from 'lodash-es';
import { format } from 'date-fns';

import { showError } from '@utils';
import { tradeTicketsServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';

import {
  TRADE_TICKETS_REASONS_OF_REJECTION,
  TRADE_TICKETS_STAGES_NAMES,
  TRADE_TICKETS_STAGES_ORDER,
} from 'components/tradeTickets/constants';

import { useAssignees } from 'hooks/common';
import {
  handleComments,
  handleChangelog,
  handlePricing,
  canAddCustomerPreferredPriceExists,
  canUpdatePrice,
  CUSTOMER_PREFERRED_PRICE,
} from './utils';

export const useTradeTicketOperations = () => {
  const router = useRouter();
  const {
    assignees,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const tradeTicketId = router.query?.tradeTicketId;

  const tradeTicketResponse = useQuery(
    [QUERY_KEYS.GET_TRADE_TICKET_KEY, tradeTicketId],
    () => tradeTicketsServices.getTradeTicket(tradeTicketId),
    {
      enabled: isAssigneesFetched && !!tradeTicketId,
    }
  );

  const tradeTicketCommentsResponse = useQuery(
    [QUERY_KEYS.GET_TRADE_TICKET_COMMENTS_KEY, tradeTicketId],
    () => tradeTicketsServices.getTicketTradeComments(tradeTicketId),
    {
      enabled: !!tradeTicketId,
    }
  );

  const tradeTicketHistoryResponse = useQuery(
    [
      QUERY_KEYS.GET_TRADE_TICKET_HISTORY_KEY,
      tradeTicketResponse?.data?.data?.internalId,
    ],
    () =>
      tradeTicketsServices.getTicketTradeChangeLog(
        tradeTicketResponse?.data?.data?.internalId
      ),
    {
      enabled: !!tradeTicketId && tradeTicketResponse.isFetched,
    }
  );
  const tradeTicketPricingResponse = useQuery(
    [QUERY_KEYS.GET_TRADE_TICKET_PRICING_KEY, tradeTicketId],
    () => tradeTicketsServices.getTicketTradePricing(tradeTicketId),
    {
      enabled: !!tradeTicketId,
    }
  );

  const tradeTicketDocumentsResponse = useQuery(
    [QUERY_KEYS.GET_TRADE_TICKET_DOCUMENTS_KEY, tradeTicketId],
    () => tradeTicketsServices.getTicketTradeFiles(tradeTicketId),
    {
      enabled: !!tradeTicketId,
    }
  );
  const updateTradeTicket = async (payload = {}) => {
    try {
      await tradeTicketsServices.updateTradeTicket(tradeTicketId, payload);
    } catch (error) {
      showError(error);
    }
  };

  const updateTradeTicketPricing = async (pricingId, payload = {}) => {
    try {
      if (!canUpdatePrice(payload, tradeTicketPricingResponse?.data?.data)) {
        showError(`Only one "${CUSTOMER_PREFERRED_PRICE}" is allowed.`);
        return;
      }
      const values = {
        ...omit(payload, ['id', 'isNew', 'createdAt']),
        amount: payload.amount ? parseFloat(payload.amount) : 0,
        date: payload.date ? format(new Date(payload.date), 'yyyy-MM-dd') : '',
      };
      await tradeTicketsServices.updateTicketTradePricing(
        tradeTicketId,
        pricingId,
        values
      );
      await tradeTicketPricingResponse.refetch();
      tradeTicketResponse.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const addTradeTicketPricing = async (payload = {}) => {
    try {
      if (
        !canAddCustomerPreferredPriceExists(
          payload,
          tradeTicketPricingResponse?.data?.data
        )
      ) {
        showError(`Only one "${CUSTOMER_PREFERRED_PRICE}" is allowed.`);
        return;
      }
      const values = {
        ...omit(payload, ['id', 'isNew', 'createdAt', 'finalized']),
        amount: payload.amount ? parseFloat(payload.amount) : 0,
        date: payload.date ? format(new Date(payload.date), 'yyyy-MM-dd') : '',
      };
      await tradeTicketsServices.addTicketTradePricing(tradeTicketId, values);
      tradeTicketPricingResponse.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const uploadFiles = async (documents = []) => {
    try {
      const uploadAndValidate = async (item) => {
        const { data } = await tradeTicketsServices.postFiles(tradeTicketId, {
          filename: item.file.name,
        });
        await tradeTicketsServices.uploadFilesToS3(
          data.url,
          item.file,
          item.file.type
        );
        await tradeTicketsServices.validateFiles(tradeTicketId, data.id);
      };

      await Promise.all(documents.map(uploadAndValidate));
    } catch (error) {
      showError(error);
    }
  };

  const getTradeTicket = async () => {
    await tradeTicketResponse.refetch();
    tradeTicketHistoryResponse.refetch();
  };

  const tradeTicket = tradeTicketResponse?.data?.data || {};

  const comments = handleComments(
    tradeTicketCommentsResponse?.data?.data || []
  );

  const history = handleChangelog(tradeTicketHistoryResponse?.data?.data || []);
  const pricing = handlePricing(tradeTicketPricingResponse?.data?.data || []);

  useEffect(() => {
    if (tradeTicketResponse?.error) {
      showError(tradeTicketResponse?.error);
    }
  }, [tradeTicketResponse?.error]);

  useEffect(() => {
    if (tradeTicketPricingResponse?.error) {
      showError(tradeTicketPricingResponse?.error);
    }
  }, [tradeTicketPricingResponse?.error]);

  useEffect(() => {
    if (tradeTicketHistoryResponse?.error) {
      showError(tradeTicketHistoryResponse?.error);
    }
  }, [tradeTicketHistoryResponse?.error]);

  useEffect(() => {
    if (tradeTicketCommentsResponse?.error) {
      showError(tradeTicketCommentsResponse?.error);
    }
  }, [tradeTicketCommentsResponse?.error]);

  useEffect(() => {
    if (tradeTicketDocumentsResponse?.error) {
      showError(tradeTicketDocumentsResponse?.error);
    }
  }, [tradeTicketDocumentsResponse?.error]);

  const loading = isAssigneesFetching || tradeTicketResponse.isFetching;

  const stages = TRADE_TICKETS_STAGES_ORDER.reduce((acc, cur) => {
    acc.push({
      text: TRADE_TICKETS_STAGES_NAMES[cur].label,
      value: cur,
    });

    return acc;
  }, []);
  const documents = (tradeTicketDocumentsResponse?.data?.data || []).filter(
    (doc) => doc.type !== 'VehicleVideo'
  );
  const media = (tradeTicketDocumentsResponse?.data?.data || []).filter(
    (doc) => doc.type === 'VehicleVideo'
  );

  return {
    stages,
    loading,
    tradeTicket,
    tradeTicketId,
    assignees,
    comments,
    history,
    pricing,
    reasonsOfRejection: TRADE_TICKETS_REASONS_OF_REJECTION,
    documents,
    media,
    updateTradeTicket,
    updateTradeTicketPricing,
    addTradeTicketPricing,
    getDocuments: tradeTicketDocumentsResponse?.refetch,
    getTradeTicket,
    getHistory: tradeTicketHistoryResponse.refetch,
    getComments: tradeTicketCommentsResponse.refetch,
    getPricing: tradeTicketPricingResponse.refetch,
    uploadFiles,
  };
};

export default useTradeTicketOperations;
